import { useState } from 'react';

import {
  Button,
  Divider,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  type MenuProps,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';

import { BottomSheet } from '@/components/BottomSheet';
import { AVAILABLE_LANGUAGES } from '@/lib/i18n/constants';

export const NavBarLanguageMenu = ({
  isDisabled,
  renderAsBottomSheet,
  ...rest
}: Omit<MenuProps, 'children'> & {
  isDisabled?: boolean;
  renderAsBottomSheet?: boolean;
}) => {
  const { i18n } = useTranslation();
  const bottomSheetDisclosure = useDisclosure();

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Menu
        placement="bottom-end"
        {...rest}
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
      >
        <MenuButton
          isDisabled={isDisabled}
          bg="transparent"
          borderColor="gray.50"
          pl={3}
          pr={6}
          borderRadius="8"
          border="1px solid"
          as={Button}
          variant="unstyled"
          fontSize={{ base: 14, sm: 16 }}
          onClick={() => {
            if (renderAsBottomSheet) {
              bottomSheetDisclosure.onToggle();
            }
          }}
        >
          <HStack justifyContent="space-between" w="full" gap={2}>
            <Icon
              as={
                AVAILABLE_LANGUAGES.find(
                  (language) => language.key === i18n.language
                )?.flag
              }
              fontSize="24px"
            />
            <Stack h={5} w={5} justifyContent="center" alignItems="center">
              <Icon
                as={isOpen ? LuChevronUp : LuChevronDown}
                fontSize="lg"
                color="gray.600"
              />
            </Stack>
          </HStack>
        </MenuButton>
        {!renderAsBottomSheet && (
          <MenuList
            minW="6rem"
            overflow="hidden"
            p={3}
            borderColor="gray.50"
            shadow="md"
            borderRadius={12}
          >
            {AVAILABLE_LANGUAGES.map(({ key, flag }) => (
              <MenuItem
                borderRadius={8}
                key={key}
                onClick={() => {
                  i18n.changeLanguage(key);
                }}
                gap={4}
                py={2}
                _hover={{
                  bg: 'brand.50',
                }}
              >
                <Icon as={flag} fontSize="20px" />
                <Text fontSize="sm" fontWeight="600" color="gray.900">
                  {key.toLocaleUpperCase()}
                </Text>
              </MenuItem>
            ))}
          </MenuList>
        )}
      </Menu>

      <BottomSheet
        isOpen={bottomSheetDisclosure.isOpen}
        onClose={bottomSheetDisclosure.onClose}
      >
        <VStack divider={<Divider />} alignItems="start">
          {AVAILABLE_LANGUAGES.map(({ key, flag }) => (
            <HStack
              key={key}
              onClick={() => {
                if (!isDisabled) {
                  i18n.changeLanguage(key);
                }
              }}
              gap={4}
              py={2}
              _hover={{
                bg: 'gray.50',
              }}
              cursor="pointer"
            >
              <Icon as={flag} fontSize="16px" />
              <Text fontSize="sm" fontWeight="600" color="gray.900">
                {key.toLocaleUpperCase()}
              </Text>
            </HStack>
          ))}
        </VStack>
      </BottomSheet>
    </>
  );
};
